import React, { useState, useRef, useEffect } from 'react';
import { compressImage, MAX_PHOTO_BYTES } from './imageUtils';
import { uploadPhoto, resolvePhotoDataUrl, hasPhotoValue } from './photoStorage';
import { getFieldDisplayName } from './formSchemaUtils';

// Field names are free text, so strip anything a filename can't carry.
const photoFilename = (field) =>
  getFieldDisplayName(field).replace(/[^a-zA-Z0-9-_]+/g, '_').replace(/^_+|_+$/g, '') || field.id;

// Photo capture/preview field. In edit mode it lets the user take/choose a
// photo (compressed to a JPEG, then uploaded to object storage); the field
// value is the returned "photo:<key>" reference, not the image bytes. When
// disabled it resolves that reference back to a viewable image.
const PhotoField = ({ field, value, onChange, disabled }) => {
  const inputRef = useRef(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [displayUrl, setDisplayUrl] = useState('');
  const hasPhoto = hasPhotoValue(value);

  // Resolve the stored value (GCS ref or legacy inline data URL) to something
  // an <img>/download link can use.
  useEffect(() => {
    let cancelled = false;
    if (!hasPhotoValue(value)) {
      setDisplayUrl('');
      return undefined;
    }
    resolvePhotoDataUrl(value)
      .then((url) => {
        if (!cancelled) setDisplayUrl(url || '');
      })
      .catch(() => {
        if (!cancelled) setDisplayUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setProcessing(true);
    try {
      const dataUrl = await compressImage(file);
      if (typeof dataUrl !== 'string') throw new Error('Failed to encode image');
      if (dataUrl.length > MAX_PHOTO_BYTES) {
        setError('Image is too large after compression. Try a smaller photo.');
      } else {
        const ref = await uploadPhoto(dataUrl);
        onChange(field.id, ref);
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      setError('Could not upload this image. Please try again.');
    } finally {
      setProcessing(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (disabled) {
    if (hasPhoto) {
      if (!displayUrl) {
        return (
          <div className="bg-gray-700/40 border border-gray-600 rounded px-3 py-2 text-gray-400 italic">
            Loading photo…
          </div>
        );
      }
      return (
        <div className="space-y-2">
          <img
            src={displayUrl}
            alt={field.label || 'Submitted photo'}
            className="max-h-96 w-auto rounded border border-gray-600"
          />
          <a
            href={displayUrl}
            download={`${photoFilename(field)}.jpg`}
            className="inline-block text-sm text-purple-300 hover:text-purple-200 underline"
          >
            Download
          </a>
        </div>
      );
    }
    return (
      <div className="bg-gray-700/40 border border-gray-600 rounded px-3 py-2 text-gray-400 italic">
        No photo provided
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        id={`photo-input-${field.id}`}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="sr-only"
      />
      {hasPhoto && displayUrl && (
        <img
          src={displayUrl}
          alt={field.label || 'Selected photo'}
          className="max-h-96 w-auto rounded border border-gray-600"
        />
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <label
          htmlFor={`photo-input-${field.id}`}
          className="cursor-pointer inline-flex items-center bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 shadow"
        >
          {hasPhoto ? 'Replace photo' : 'Take photo / Choose file'}
        </label>
        {hasPhoto && (
          <button
            type="button"
            onClick={() => {
              setError('');
              onChange(field.id, '');
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
          >
            Remove
          </button>
        )}
        {processing && (
          <span className="text-sm text-gray-300">Processing image…</span>
        )}
      </div>
      {error && <div className="text-sm text-red-400">{error}</div>}
      {field.required && !hasPhoto && (
        <input
          type="text"
          required
          value=""
          onChange={() => {}}
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          onInvalid={(e) => e.target.setCustomValidity('Please add a photo for this field.')}
          onInput={(e) => e.target.setCustomValidity('')}
        />
      )}
    </div>
  );
};

export default PhotoField;
